<template>
  <div class="home-subtitle" :class="{ 'is-english': $i18n.locale === 'en' }">
    <p v-for="(line, lineIndex) in lines" :key="lineIndex" class="subtitle-line">
      <span
        v-for="(word, wordIndex) in line.words"
        :key="`${lineIndex}-${wordIndex}`"
        :class="{
          highlight: word.highlightable
            && currentLineIndex === lineIndex
            && currentWordIndex === wordIndex
        }"
        class="subtitle-word"
      >
        {{ word.text }}
      </span>
    </p>
  </div>
</template>

<script>
export default {
  name: 'HomeSubtitle',
  props: {
    text: {
      type: String,
      required: true
    },
    speed: {
      type: Number,
      default: 600
    }
  },
  data() {
    return {
      lines: [],
      currentLineIndex: 0,
      currentWordIndex: 0,
      timer: null
    }
  },
  watch: {
    text: {
      immediate: true,
      handler(newText) {
        const processedText = (newText || '').replace(/\\n/g, '\n');
        this.lines = processedText
          .split('\n')
          .filter(Boolean)
          .map((line) => ({
            text: line,
            words: this.splitLine(line)
          }));
        this.startAnimation();
      }
    }
  },
  methods: {
    splitLine(line) {
      const phraseChunks = {
        '把任意音频变成单词级字幕': [
          '把任意音频',
          '变成',
          '单词级字幕'
        ],
        '再按句精听 跟读 评分': [
          '再按句',
          '精听',
          '跟读',
          '评分'
        ]
      };
      const chunks = phraseChunks[line] || line.match(
        /[\p{Script=Han}]{1,4}[，。、；：！？]?|[A-Za-z0-9+'’-]+[,.!?;:]?/gu
      ) || [line];

      return chunks.map((chunk) => ({
        text: chunk,
        highlightable: /[\p{Script=Han}A-Za-z0-9]/u.test(chunk)
      }));
    },
    getFirstHighlightIndex(line) {
      return line?.words.findIndex((word) => word.highlightable) ?? -1;
    },
    moveToNextLine() {
      for (let offset = 1; offset <= this.lines.length; offset++) {
        const lineIndex = (this.currentLineIndex + offset) % this.lines.length;
        const wordIndex = this.getFirstHighlightIndex(this.lines[lineIndex]);

        if (wordIndex >= 0) {
          this.currentLineIndex = lineIndex;
          this.currentWordIndex = wordIndex;
          return;
        }
      }
    },
    startAnimation() {
      this.stopAnimation();
      this.currentLineIndex = 0;
      this.currentWordIndex = this.getFirstHighlightIndex(this.lines[0]);

      if (this.currentWordIndex < 0) {
        this.moveToNextLine();
      }

      this.timer = setInterval(() => {
        const currentLine = this.lines[this.currentLineIndex];
        if (!currentLine) return;

        const nextWordIndex = currentLine.words.findIndex(
          (word, index) => index > this.currentWordIndex && word.highlightable
        );

        if (nextWordIndex >= 0) {
          this.currentWordIndex = nextWordIndex;
          return;
        }

        this.moveToNextLine();
      }, this.speed);
    },
    stopAnimation() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    }
  },
  beforeUnmount() {
    this.stopAnimation();
  }
}
</script>

<style scoped>
.home-subtitle {
  max-width: 680px;
  margin: 1.5rem 0 0;
  color: var(--ink-primary);
  font-size: clamp(1.2rem, 1.4vw, 1.375rem);
  font-weight: 500;
  line-height: 1.6;
  text-wrap: pretty;
}

.subtitle-line {
  min-height: 1.6em;
  margin: 0.2rem 0;
}

.subtitle-word {
  display: inline-block;
  margin: 0 0.12rem;
  padding: 0.02rem 0.16rem;
  border-radius: 4px;
  transition: color 0.2s ease, background-color 0.2s ease, transform 0.2s ease;
}

.subtitle-word.highlight {
  color: var(--ink-primary);
  background: var(--signal-active);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--signal-active) 18%, transparent);
  transform: translateY(-1px);
}


.home-subtitle.is-english .subtitle-line {
  margin: 0;
}

.home-subtitle.is-english .subtitle-word {
  margin: 0;
  padding: 0 0.12rem;
}

@media screen and (max-width: 860px) {
  .home-subtitle {
    margin-left: auto;
    margin-right: auto;
  }
}

@media screen and (max-width: 640px) {
  .home-subtitle {
    font-size: 1.08rem;
    line-height: 1.6;
  }
}
</style>
